import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'text'
    | 'figma-primary'
    | 'figma-secondary'
    | 'lms-primary'
    | 'lms-secondary'
    | 'lms-secondary-primary'
    | 'lms-danger'
    | 'lms-danger-primary'
    | 'lms-success-primary';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const baseClasses =
      'inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variantClasses = {
      primary:
        'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 rounded-lg',
      secondary:
        'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50 focus:ring-gray-500 rounded-lg',
      outline:
        'bg-transparent text-white border border-white hover:bg-white/10 focus:ring-white rounded-lg',
      text: 'bg-transparent text-primary-600 hover:bg-primary-50 focus:ring-primary-500 rounded-lg',
      'figma-primary':
        'bg-primary-6 text-character-primary-inverse hover:bg-[--color-primary-hover] focus:ring-primary-6 border border-primary-6 rounded-[2px] shadow-[0_2px_0_0_rgba(0,0,0,0.04)] font-roboto font-normal',
      'figma-secondary':
        'bg-white text-character-title-85 border border-[#d9d9d9] hover:border-primary-6 hover:text-primary-6 focus:ring-primary-6 rounded-[2px] shadow-[0_2px_0_0_rgba(0,0,0,0.02)] font-roboto font-normal',
      /* Updated LMS design system buttons (Figma "LMS Portal - Updated"):
         rounded-6, Roboto Medium 14/20, icon gap 8. */
      'lms-primary':
        'bg-primary-6 text-white border border-primary-6 hover:bg-[--color-primary-hover] focus:ring-primary-6 rounded-[6px] font-roboto gap-2',
      /* Disabled state per Figma "Secondary Button / Disabled": grey fill. */
      'lms-secondary':
        'bg-white text-neutral-n800 border border-lms-border hover:border-primary-6 hover:text-primary-6 focus:ring-primary-6 rounded-[6px] font-roboto gap-2 disabled:bg-lms-disabled-bg disabled:border-lms-border disabled:text-neutral-n200 disabled:opacity-100',
      /* Figma "Secondary Button + Icon" (purple outline, e.g. Add Flag):
         Primary/Purple Hover 2 border + text on white. */
      'lms-secondary-primary':
        'bg-white text-lms-purple-600 border border-lms-purple-600 hover:bg-lms-purple-50 focus:ring-lms-purple-600 rounded-[6px] font-roboto gap-2 disabled:bg-lms-disabled-bg disabled:border-lms-border disabled:text-neutral-n200 disabled:opacity-100',
      'lms-danger':
        'bg-white text-error-500 border border-lms-red-200 hover:bg-error-50 focus:ring-error-500 rounded-[6px] font-roboto gap-2',
      /* Figma "Primary Button Danger": enabled #EF4444, disabled #F4B6B6. */
      'lms-danger-primary':
        'bg-error-500 text-white border border-error-500 hover:bg-lms-red-hover hover:border-lms-red-hover focus:ring-error-500 rounded-[6px] font-roboto gap-2 disabled:bg-lms-red-200 disabled:border-lms-red-200 disabled:opacity-100',
      /* Figma "Primary Button Reactivate Account": Status/Green/Green1. */
      'lms-success-primary':
        'bg-polar-green-6 text-white border border-polar-green-6 hover:bg-polar-green-6/90 hover:border-polar-green-6/90 focus:ring-polar-green-6 rounded-[6px] font-roboto gap-2',
    };

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-6 py-3 text-lg',
    };

    const figmaSizeClasses = {
      sm: 'px-3 py-1 text-[12px] leading-[20px]',
      md: 'px-4 py-[5px] text-[14px] leading-[22px]',
      lg: 'px-5 py-2 text-[16px] leading-[24px]',
    };

    /* Figma specs: md = Secondary/Primary Button (px-17/py-9),
       sm = compact "View Details" style (px-13/py-7). */
    const lmsSizeClasses = {
      sm: 'px-[13px] py-[7px] text-[14px] leading-[20px]',
      md: 'px-[17px] py-[9px] text-[14px] leading-[20px]',
      lg: 'px-[21px] py-[11px] text-[16px] leading-[24px]',
    };

    const isFigmaVariant =
      variant === 'figma-primary' || variant === 'figma-secondary';
    const isLmsVariant =
      variant === 'lms-primary' ||
      variant === 'lms-secondary' ||
      variant === 'lms-danger' ||
      variant === 'lms-danger-primary' ||
      variant === 'lms-success-primary';

    return (
      <button
        ref={ref}
        className={cn(
          baseClasses,
          variantClasses[variant],
          isFigmaVariant
            ? figmaSizeClasses[size]
            : isLmsVariant
              ? lmsSizeClasses[size]
              : sizeClasses[size],
          loading && 'cursor-wait',
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading...
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
